import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { ArrowRight, Linkedin, Mail } from 'lucide-react';

const Leadership = () => {
  const leaders = [
    {
      id: 'keyvin-runtuwarouw',
      name: 'Keyvin Runtuwarouw',
      title: 'Chief Executive Officer',
      image: '/assets/Keyvin%20Runtuwarouw%204x3%20Small.jpg',
      shortBio: 'Visionary leader driving innovation and growth at Vincotek with extensive experience in technology and business strategy.',
      expertise: ['Strategic Leadership', 'Technology Innovation', 'Business Development', 'Digital Transformation'],
      email: 'keyvin@vincotek.com',
      linkedin: '#'
    },
    {
      id: 'Rey-Runtuwarouw',
      name: 'Rey Runtuwarouw',
      title: 'Chief Corporate Officer',
      image: '/assets/Margareta%20Tanuwijaya%204x3%20Small.jpg',
      shortBio: 'Strategic executive focused on corporate development, partnerships, and organizational excellence.',
      expertise: ['Corporate Strategy', 'Partnership Development', 'Operations Excellence', 'Team Leadership'],
      email: 'rey@vincotek.com',
      linkedin: '#'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-white via-blue-50/30 to-yellow-50/20 py-20 lg:py-32">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-vincotek-dark leading-tight mb-6">
              Our{' '}
              <span className="bg-gradient-to-r from-vincotek-yellow to-vincotek-blue bg-clip-text text-transparent">
                Leadership
              </span>{' '}
              Team
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed max-w-3xl mx-auto">
              Meet the visionary leaders who are driving Vincotek's mission to empower technology and enable growth. 
              Our leadership team brings together diverse expertise, innovative thinking, and a shared commitment to making 
              a positive impact through technology.
            </p>
          </div>
        </div>
      </section>

      {/* Leadership Profiles */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {leaders.map((leader, index) => (
              <div key={leader.id} className={`${index % 2 === 1 ? 'lg:mt-16' : ''}`}>
                <Card className="group hover:shadow-2xl transition-all duration-500 hover:-translate-y-4 bg-white border-0 overflow-hidden">
                  <CardContent className="p-0">
                    {/* Image Section */}
                    <div className="relative overflow-hidden">
                      <img 
                        src={leader.image} 
                        alt={leader.name}
                        className="w-full h-80 object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    </div>
                    
                    {/* Content Section */}
                    <div className="p-8">
                      <div className="mb-6">
                        <h3 className="text-2xl font-bold text-vincotek-dark mb-2">
                          {leader.name}
                        </h3>
                        <p className="text-vincotek-blue font-semibold text-lg">
                          {leader.title}
                        </p>
                      </div>
                      
                      <p className="text-gray-600 leading-relaxed mb-6">
                        {leader.shortBio}
                      </p>
                      
                      {/* Expertise */}
                      <div className="mb-6">
                        <h4 className="text-sm font-semibold text-vincotek-dark mb-3 uppercase tracking-wide">
                          Expertise
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {leader.expertise.map((skill, skillIndex) => (
                            <span
                              key={skillIndex}
                              className="bg-gradient-to-r from-vincotek-yellow/20 to-vincotek-blue/20 text-vincotek-dark px-3 py-1 rounded-full text-sm font-medium"
                            >
                              {skill}
                            </span>
                          ))}
                        </div>
                      </div>
                      
                      {/* Contact Info */}
                      <div className="flex items-center justify-between">
                        <div className="flex space-x-4">
                          <a 
                            href={`mailto:${leader.email}`}
                            className="text-gray-400 hover:text-vincotek-blue transition-colors"
                          >
                            <Mail className="h-5 w-5" />
                          </a>
                          <a 
                            href={leader.linkedin}
                            className="text-gray-400 hover:text-vincotek-blue transition-colors"
                          >
                            <Linkedin className="h-5 w-5" />
                          </a>
                        </div>
                        
                        <Link to={`/about/leadership/${leader.id}`}>
                          <Button 
                            variant="outline" 
                            className="border-vincotek-blue text-vincotek-blue hover:bg-vincotek-blue hover:text-white rounded-full transition-all duration-300 group"
                          >
                            View Profile
                            <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Leadership Philosophy */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50/30">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-vincotek-dark mb-6">
                Our Leadership Philosophy
              </h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <Card className="text-center p-8 bg-white border-0 shadow-lg hover:shadow-xl transition-all duration-300">
                <div className="w-16 h-16 bg-gradient-to-br from-vincotek-yellow to-vincotek-blue rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-white font-bold text-xl">1</span>
                </div>
                <h3 className="text-xl font-semibold text-vincotek-dark mb-4">
                  Lead by Example
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  We believe in demonstrating the values and behaviors we expect from our team, 
                  fostering a culture of integrity and excellence.
                </p>
              </Card>
              
              <Card className="text-center p-8 bg-white border-0 shadow-lg hover:shadow-xl transition-all duration-300">
                <div className="w-16 h-16 bg-gradient-to-br from-vincotek-blue to-vincotek-yellow rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-white font-bold text-xl">2</span>
                </div>
                <h3 className="text-xl font-semibold text-vincotek-dark mb-4">
                  Empower Others
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  Our leadership style focuses on empowering team members to reach their full potential 
                  and contribute meaningfully to our shared mission.
                </p>
              </Card>
              
              <Card className="text-center p-8 bg-white border-0 shadow-lg hover:shadow-xl transition-all duration-300">
                <div className="w-16 h-16 bg-gradient-to-br from-vincotek-yellow to-vincotek-blue rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-white font-bold text-xl">3</span>
                </div>
                <h3 className="text-xl font-semibold text-vincotek-dark mb-4">
                  Drive Innovation
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  We encourage creative thinking, calculated risk-taking, and continuous learning 
                  to stay at the forefront of technological advancement.
                </p>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-vincotek-dark text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Want to Connect with Our Leaders?
          </h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            Our leadership team is always open to connecting with like-minded individuals, 
            potential partners, and anyone interested in our mission.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button className="bg-vincotek-yellow hover:bg-yellow-400 text-vincotek-dark px-8 py-3 rounded-full font-semibold transition-all duration-300 hover:shadow-lg">
                Get in Touch
              </Button>
            </Link>
            <Link to="/about">
              <Button variant="outline" className="border-white text-white hover:bg-white hover:text-vincotek-dark px-8 py-3 rounded-full transition-all duration-300">
                Learn More About Us
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Leadership;